import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MultiSeriesTimeChart, { SERIES_PALETTE } from '../../../components/chart/MultiSeriesTimeChart';
import { getSensorGroupKey } from '../../../services/localizationService';

// The recording's chart. It used to take a "primary" and a "secondary" sensor,
// because two was as many axes as the hand-written options could carry. The
// axes are derived from the selection now, so a reader can put as many
// quantities on the one time axis as the recording holds — each on its own
// coloured, named axis (see MultiSeriesTimeChart).

const EVENT_COLOR = '#d50100';
const PLAYHEAD_COLOR = '#775DD0';

SessionChartTab.propTypes = {
  channels: PropTypes.array,
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
  onSelectedKeysChange: PropTypes.func,
  events: PropTypes.array,
  currentTime: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
  onChartClick: PropTypes.func,
  eventSensor: PropTypes.string,
  setSensor: PropTypes.func,
};

export default function SessionChartTab({
  channels = [],
  // The selection is held by the parent: one chart per tab, each keeping its
  // own channels while the others are unmounted.
  selectedKeys = [],
  onSelectedKeysChange,
  events = [],
  currentTime,
  onChartClick,
  eventSensor = null,
  setSensor,
}) {
  // Clicking an event in the timeline is a request to look at the channel that
  // raised it, so it replaces the selection rather than adding to it.
  useEffect(() => {
    if (!eventSensor) return;
    const match = channels.find(
      (c) => c.channel === eventSensor || getSensorGroupKey(c.channel) === getSensorGroupKey(eventSensor)
    );
    if (match && onSelectedKeysChange) onSelectedKeysChange([match.key]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSensor, channels]);

  const selected = selectedKeys.map((k) => channels.find((c) => c.key === k)).filter(Boolean);

  // The colour is assigned here rather than left to the chart's default so the
  // chip in the selector and the line and axis it stands for cannot drift
  // apart — one source, read by both.
  const colorFor = (i) => SERIES_PALETTE[i % SERIES_PALETTE.length];

  const series = selected.map((c, i) => ({
    id: c.key,
    name: c.name,
    unit: c.unit || undefined,
    color: colorFor(i),
    data: c.points,
  }));

  const annotations = useMemo(
    () => ({
      xaxis: [
        ...events.map((event) => ({
          x: new Date(event.startTime).getTime(),
          x2: new Date(event.endTime).getTime(),
          fillColor: EVENT_COLOR,
          label: { text: event.label },
        })),
        {
          x: new Date(currentTime).getTime(),
          borderColor: PLAYHEAD_COLOR,
          label: {
            borderColor: PLAYHEAD_COLOR,
            style: { color: '#fff', background: PLAYHEAD_COLOR },
            text: 'Current Time',
          },
        },
      ],
    }),
    [events, currentTime]
  );

  if (channels.length === 0) {
    return (
      <Typography sx={{ py: 6 }} variant="h6" align="center">
        No charts to display
      </Typography>
    );
  }

  return (
    <Box sx={{ overflow: 'visible' }}>
      <Box sx={{ px: 3, pt: 1 }}>
        <Autocomplete
          multiple
          disablePortal
          id="combo-box-session-channels"
          options={channels}
          value={selected}
          getOptionLabel={(c) => c.name}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          onChange={(_, picked) => {
            if (onSelectedKeysChange) onSelectedKeysChange(picked.map((c) => c.key));
            // The event selection has been overridden by hand; leaving it set
            // would re-apply it on the next render.
            if (setSensor) setSensor(null);
          }}
          renderTags={(picked, getTagProps) =>
            picked.map((option, index) => {
              const color = colorFor(index);
              const tagProps = getTagProps({ index });
              // The key is passed explicitly below; spreading it warns.
              delete tagProps.key;
              return (
                <Chip
                  {...tagProps}
                  key={option.key}
                  label={option.name}
                  size="small"
                  style={{ color, backgroundColor: alpha(color, 0.16) }}
                  sx={{ fontWeight: 600, '& .MuiChip-deleteIcon': { color, opacity: 0.7 } }}
                />
              );
            })
          }
          renderInput={(params) => <TextField {...params} label="Sensors" placeholder="Add a sensor" />}
        />
      </Box>
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <MultiSeriesTimeChart
          series={series}
          height={260}
          annotations={annotations}
          onPointClick={(t) => onChartClick && onChartClick({ startTime: t })}
          emptyMessage="Pick a sensor to chart"
        />
      </Box>
    </Box>
  );
}
